import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, ChevronUp, Lightbulb, BookOpen, Zap, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/shared/utils';

export interface GuideSection {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'tip' | 'warning' | 'best-practice';
  importance: 'low' | 'medium' | 'high';
  estimatedReadTime?: number; // in minutes
  actionable?: {
    buttonText: string;
    action: () => void;
  };
}

export interface ContextualGuideProps {
  title: string;
  description?: string;
  sections: GuideSection[];
  position?: 'sidebar' | 'modal' | 'inline' | 'floating';
  size?: 'sm' | 'md' | 'lg';
  isVisible: boolean;
  onClose?: () => void;
  onSectionComplete?: (sectionId: string) => void;
  showProgress?: boolean;
  allowCollapse?: boolean;
  autoExpand?: boolean;
  className?: string;
  category?: 'getting-started' | 'feature-help' | 'troubleshooting' | 'advanced';
}

export const ContextualGuide: React.FC<ContextualGuideProps> = ({
  title,
  description,
  sections,
  position = 'sidebar',
  size = 'md',
  isVisible,
  onClose,
  onSectionComplete,
  showProgress = true,
  allowCollapse = true,
  autoExpand = false,
  className,
  category = 'feature-help',
}) => {
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    autoExpand ? new Set(sections.map((s) => s.id)) : new Set(),
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  // Calculate progress
  const progress = (completedSections.size / sections.length) * 100;

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, onClose]);

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleSectionComplete = (sectionId: string) => {
    setCompletedSections((prev) => new Set([...prev, sectionId]));
    if (onSectionComplete) {
      onSectionComplete(sectionId);
    }
  };

  const getSectionIcon = (type: GuideSection['type']) => {
    switch (type) {
      case 'tip':
        return <Lightbulb className="w-4 h-4 text-yellow-500" />;
      case 'warning':
        return <Info className="w-4 h-4 text-orange-500" />;
      case 'best-practice':
        return <Zap className="w-4 h-4 text-green-500" />;
      case 'info':
      default:
        return <BookOpen className="w-4 h-4 text-blue-500" />;
    }
  };

  const getSectionBadgeColor = (importance: GuideSection['importance']) => {
    switch (importance) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:hover:bg-yellow-800';
      case 'low':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700';
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'modal':
        return 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs';
      case 'inline':
        return 'w-full';
      case 'floating':
        return 'fixed top-4 right-4 z-40';
      case 'sidebar':
      default:
        return 'h-full';
    }
  };

  const getSizeClasses = () => {
    const baseClasses =
      'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg';

    switch (size) {
      case 'sm':
        return cn(baseClasses, 'max-w-sm');
      case 'lg':
        return cn(baseClasses, 'max-w-2xl');
      case 'md':
      default:
        return cn(baseClasses, 'max-w-lg');
    }
  };

  const getCategoryIcon = () => {
    switch (category) {
      case 'getting-started':
        return '🚀';
      case 'troubleshooting':
        return '🔧';
      case 'advanced':
        return '⚡';
      case 'feature-help':
      default:
        return '💡';
    }
  };

  if (!isVisible) return null;

  const content = (
    <div
      ref={guideRef}
      className={cn(
        getSizeClasses(),
        position === 'modal' && 'max-h-[80vh] overflow-hidden',
        className,
      )}
    >
      {/* Header */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{getCategoryIcon()}</span>
              <CardTitle className="text-lg font-semibold">{title}</CardTitle>
            </div>
            {description && (
              <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                {description}
              </CardDescription>
            )}
          </div>

          <div className="flex items-center gap-1">
            {allowCollapse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 h-auto"
                aria-label={isCollapsed ? 'Expand guide' : 'Collapse guide'}
              >
                {isCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </Button>
            )}
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="p-1 h-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Close guide"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {showProgress && !isCollapsed && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Progress</span>
              <span>
                {completedSections.size} of {sections.length} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </CardHeader>

      {/* Content */}
      {!isCollapsed && (
        <CardContent
          className={cn('space-y-3', position === 'modal' && 'max-h-[60vh] overflow-y-auto')}
        >
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const isCompleted = completedSections.has(section.id);

            return (
              <Card
                key={section.id}
                className={cn(
                  'border transition-all duration-200',
                  isCompleted
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
                    : 'border-gray-200 dark:border-gray-700',
                  isExpanded && 'shadow-xs',
                )}
              >
                <Collapsible open={isExpanded} onOpenChange={() => handleSectionToggle(section.id)}>
                  <CollapsibleTrigger asChild>
                    <button className="w-full p-3 text-left focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getSectionIcon(section.type)}
                          <span
                            className={cn(
                              'font-medium text-sm',
                              isCompleted && 'line-through text-gray-500',
                            )}
                          >
                            {section.title}
                          </span>
                          {section.estimatedReadTime && (
                            <span className="text-xs text-gray-500">
                              ({section.estimatedReadTime}m)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            className={cn('text-xs', getSectionBadgeColor(section.importance))}
                          >
                            {section.importance}
                          </Badge>
                          {isCompleted ? (
                            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          ) : (
                            <ChevronDown
                              className={cn(
                                'w-4 h-4 transition-transform',
                                isExpanded && 'transform rotate-180',
                              )}
                            />
                          )}
                        </div>
                      </div>
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="px-3 pb-3">
                      <div className="pl-6 space-y-3">
                        <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                          {section.content}
                        </div>

                        <div className="flex items-center justify-between">
                          {section.actionable && (
                            <Button
                              size="sm"
                              onClick={section.actionable.action}
                              className="text-xs"
                            >
                              {section.actionable.buttonText}
                            </Button>
                          )}

                          {!isCompleted && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSectionComplete(section.id)}
                              className="ml-auto text-xs"
                            >
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}

          {/* Completion message */}
          {completedSections.size === sections.length && (
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                  Great job! You've completed this guide.
                </span>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </div>
  );

  if (position === 'modal') {
    return <div className={getPositionClasses()}>{content}</div>;
  }

  return <div className={cn(getPositionClasses(), className)}>{content}</div>;
};

export default ContextualGuide;
